import { useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';

import { AppShell } from '@/shared/layout/app-shell';
import { Alert } from '@/shared/ui/alert';
import { PageSkeleton } from '@/shared/ui/skeleton';

import {
  useBillingPortal,
  useBillingStatus,
  useCheckoutReturn,
  useConfirmCheckout,
  useStartCheckout,
} from '../api/use-billing';
import { PlanCards } from '../components/plan-cards';
import { PlanSummary } from '../components/plan-summary';

import type { BillingPlanId } from '@mio/shared';

import '@/shared/ui/curriculum/curriculum.css';
import '../account.css';

export function PlanPage() {
  const billing = useBillingStatus();
  const checkout = useStartCheckout();
  const confirm = useConfirmCheckout();
  const portal = useBillingPortal();
  const returned = useCheckoutReturn();
  const navigate = useNavigate();
  const [busyPlan, setBusyPlan] = useState<BillingPlanId | null>(null);
  const [notice, setNotice] = useState<'confirmed' | 'cancelled' | null>(
    returned.outcome === 'cancelled' ? 'cancelled' : null,
  );
  const confirmed = useRef(false);
  useEffect(() => {
    if (confirmed.current || returned.outcome !== 'success' || !returned.sessionId) return;
    confirmed.current = true;
    confirm.mutate(returned.sessionId, {
      onSuccess: () => {
        setNotice('confirmed');
        void navigate({ to: '/account/plan', replace: true });
      },
    });
  }, [confirm, navigate, returned]);
  function select(plan: BillingPlanId) {
    setBusyPlan(plan);
    checkout.mutate(plan, {
      onSuccess: (redirect) => window.location.assign(redirect.url),
      onError: () => setBusyPlan(null),
    });
  }
  const status = billing.data;
  return (
    <AppShell title="Plan">
      <div className="studio-page account-page">
        <header className="studio-page-heading">
          <div>
            <p className="studio-eyebrow">Plan</p>
            <h1>Choose how you continue</h1>
            <p className="studio-page-intro">
              One residency, two ways to pay. Switch or cancel from the billing portal at any time.
            </p>
          </div>
        </header>
        <div className="space-y-6">
          {notice === 'confirmed' ? (
            <Alert tone="info">Payment confirmed. Your access has been updated.</Alert>
          ) : null}
          {notice === 'cancelled' ? (
            <Alert tone="info">Checkout was cancelled. Nothing was charged.</Alert>
          ) : null}
          {confirm.isPending ? (
            <Alert tone="info">Confirming your payment with Stripe…</Alert>
          ) : null}
          {confirm.error ? (
            <Alert>
              We could not confirm the payment yet. {confirm.error.message} Your access updates
              automatically once Stripe notifies us.
            </Alert>
          ) : null}
          {checkout.error ? <Alert>{checkout.error.message}</Alert> : null}
          {portal.error ? <Alert>{portal.error.message}</Alert> : null}
          {status ? (
            status.enabled ? (
              <>
                <section className="ui-card account-section" aria-labelledby="current-plan-title">
                  <h2 id="current-plan-title">Current plan</h2>
                  <PlanSummary
                    status={status}
                    portalBusy={portal.isPending}
                    onPortal={() =>
                      portal.mutate(undefined, {
                        onSuccess: (redirect) => window.location.assign(redirect.url),
                      })
                    }
                  />
                </section>
                <section aria-labelledby="plans-title">
                  <h2 id="plans-title" className="sr-only">
                    Plans
                  </h2>
                  <PlanCards
                    plans={status.plans}
                    status={status}
                    busyPlan={busyPlan}
                    disabled={confirm.isPending}
                    onSelect={select}
                  />
                </section>
              </>
            ) : (
              <p className="text-fg-muted text-sm">Billing is not enabled in this environment.</p>
            )
          ) : billing.error ? (
            <Alert>We could not load your plan. {billing.error.message}</Alert>
          ) : (
            <PageSkeleton label="Loading plans…" />
          )}
        </div>
      </div>
    </AppShell>
  );
}
